import { useCallback, useState } from 'react';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import { handleRequestError } from 'Shared/utils/handle-request-error';
import { inviteToWorkspace, resendInvite } from 'Shared/api';
import { useDispatch } from 'react-redux';
import TeamInfoConstants from 'Shared/constants/TeamInfo.constants';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Types = {
  redirect?: boolean;
  setFullName?: boolean;
  onSuccess?: Function;
  onError?: Function;
  inviteMethod?: string;
};

type RequestTypes = {
  workspaceId: string;
  personalMessage?: string;
  workspaceName?: string;
  emails: Array<string>;
  userId: string;
};

export function useInviteToWorkspace(options?: Types): [
  Function,
  {
    loading: boolean;
    error: Error;
    data: any;
  }
] {
  const { onSuccess, onError, redirect = false, setFullName = false, inviteMethod = '' } = options || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const dispatch = useDispatch();
  const { tr } = useTranslation();

  const request = useCallback(
    async ({ workspaceId, personalMessage = '', workspaceName, emails }: RequestTypes) => {
      if (!workspaceId || !emails)
        throw new Error('Not enough input arguments, make sure "workspaceId" and "emails" exist');
      setLoading(true);
      await handleRequestError(
        inviteToWorkspace({ id: workspaceId, workspaceName, emails, personalMessage, setFullName, inviteMethod })
      )
        .then(response => {
          dispatch({ type: 'WORKSPACE_UPDATES_GET_RESPONSE', payload: response.data.workspace });
          const billingData = response.data.billingData;
          dispatch({
            type: 'UPDATE_ORGANIZATION_RESPONSE',
            payload: {
              organization: { billingState: billingData.billingState, seatsToBillFor: billingData.seatsToBillFor },
            },
          });
          dispatch({ type: TeamInfoConstants.Actions.CLEAR_TEAM_INFO, payload: { workspaceSlug: workspaceId } });
          if (onSuccess) onSuccess(response);
          setData(response);
          WpToastManager.success(tr('perform.hooks_and_toasts.invite.user_invited'));
        })
        .catch(e => {
          if (onError) onError(e);
          setError(e);
          WpToastManager.danger(e.message);
        })
        .finally(response => {
          setLoading(false);
          if (!redirect) return;
          if (response.data.redirect_path) {
            window.location.href = response.data.redirect_path;
            return;
          }
          window.location.reload();
        });
    },
    [setFullName, dispatch, onSuccess, onError, redirect]
  );
  return [request, { loading, error, data }];
}

export function useResendInvite(options?: Types): [
  Function,
  {
    loading: boolean;
    error: Error;
    data: any;
  }
] {
  const { onSuccess, onError } = options || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const { tr } = useTranslation();

  const request = useCallback(
    async ({ workspaceId, userId }) => {
      if (!workspaceId || !userId)
        throw new Error('Not enough input arguments, make sure "workspaceId" and "userId" exist');
      setLoading(true);
      await handleRequestError(resendInvite({ workspaceId, userId }))
        .then(response => {
          if (onSuccess) onSuccess(response);
          setData(response);
          WpToastManager.success(tr('perform.hooks_and_toasts.invite.resent'));
        })
        .catch(e => {
          if (onError) onError(e);
          setError(e);
          WpToastManager.danger(e.message);
          return e;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [onSuccess, onError]
  );
  return [request, { loading, error, data }];
}
